import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Mail, CheckCircle2, ArrowRight, Loader2, FileText } from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Loading overlay component - uses Portal to render at document root
const LoadingOverlay = ({ progress }) => {
  return ReactDOM.createPortal(
    <div 
      style={{ 
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.9)',
        zIndex: 99999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backdropFilter: 'blur(4px)'
      }}
    >
      <div style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        padding: '32px',
        maxWidth: '400px',
        margin: '16px',
        textAlign: 'center',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
      }}>
        <div style={{ position: 'relative', width: '80px', height: '80px', margin: '0 auto 24px' }}>
          {/* Spinning loader */}
          <Loader2 
            style={{ 
              width: '80px', 
              height: '80px', 
              color: '#3B82F6',
              animation: 'spin 1s linear infinite'
            }} 
          />
          {/* Document icon in center */}
          <div style={{ 
            position: 'absolute', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center' 
          }}>
            <FileText style={{ width: '32px', height: '32px', color: '#1E293B' }} />
          </div>
        </div>
        
        <h3 style={{ 
          fontSize: '20px', 
          fontWeight: 'bold', 
          color: '#1E293B', 
          marginBottom: '8px' 
        }}>
          PDF wird erstellt...
        </h3>
        <p style={{ color: '#64748B', marginBottom: '16px', fontSize: '14px' }}>
          Ihre personalisierte Finanzanalyse wird generiert. 
          Dies kann bis zu <span style={{ fontWeight: '600', color: '#1E293B' }}>10 Sekunden</span> dauern.
        </p>
        
        {/* Progress bar */}
        <div style={{ 
          width: '100%', 
          height: '8px', 
          backgroundColor: '#E2E8F0', 
          borderRadius: '4px', 
          overflow: 'hidden',
          marginBottom: '8px'
        }}>
          <div 
            style={{ 
              height: '100%',
              width: `${Math.min(progress, 100)}%`,
              background: 'linear-gradient(90deg, #3B82F6, #10B981)',
              borderRadius: '4px',
              transition: 'width 0.5s ease-out'
            }}
          />
        </div>
        <p style={{ fontSize: '12px', color: '#94A3B8' }}>Bitte warten Sie...</p>
      </div>
      
      {/* CSS for spinner animation */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>,
    document.body
  );
};

export const LeadCaptureForm = ({ analysisData }) => {
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  // Simulated progress during loading
  useEffect(() => {
    let interval;
    if (loading) {
      setProgress(0);
      interval = setInterval(() => {
        setProgress(prev => {
          // Slowly increase to 90%, then pause (actual completion will jump to 100%)
          if (prev < 90) {
            return prev + Math.random() * 15;
          }
          return prev;
        });
      }, 800);
    } else {
      setProgress(0);
    }
    return () => clearInterval(interval);
  }, [loading]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Step 1: Save analysis with name
      const saveResponse = await axios.post(`${API}/analysis/save`, {
        email,
        first_name: firstName,
        last_name: lastName,
        analysis_result: analysisData
      });
      
      // Get analysis_id from response or from analysisData
      const analysisId = saveResponse.data?.analysis_id || analysisData?.analysis_id;
      
      // Step 2: Send email (don't fail if this fails)
      if (analysisId) {
        try {
          await axios.post(`${API}/emails/send-analysis`, {
            email,
            first_name: firstName,
            last_name: lastName,
            analysis_id: analysisId
          });
        } catch (emailErr) {
          console.warn('Email sending failed:', emailErr);
          // Continue anyway - saving worked
        }
      }

      // Step 3: Capture lead (don't fail if this fails)
      try {
        await axios.post(`${API}/leads/capture?email=${encodeURIComponent(email)}&source=financial_app&first_name=${encodeURIComponent(firstName)}&last_name=${encodeURIComponent(lastName)}`);
      } catch (leadErr) {
        console.warn('Lead capture failed:', leadErr);
        // Continue anyway
      }

      setProgress(100);
      setTimeout(() => setSuccess(true), 300);
    } catch (err) {
      console.error('Save error:', err.response?.data || err.message);
      setError('Fehler beim Speichern. Bitte versuchen Sie es erneut.');
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="bg-emerald/10 border border-emerald/20 rounded-xl p-8 text-center mb-12" data-testid="lead-success">
        <div className="w-16 h-16 bg-emerald rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle2 className="w-8 h-8 text-white" />
        </div>
        <h3 className="font-outfit text-2xl font-bold text-navy mb-3">
          Analyse gespeichert!
        </h3>
        <p className="text-charcoal leading-relaxed mb-6">
          Wir haben Ihre Finanzanalyse an <strong>{email}</strong> gesendet.
        </p>
        <button
          onClick={() => navigate('/strategiegespraech')}
          className="px-8 py-4 bg-navy text-white rounded-full font-medium hover:bg-charcoal transition-all duration-300 inline-flex items-center gap-2"
          data-testid="goto-booking-btn"
        >
          Strategiegespräch vereinbaren
          <ArrowRight className="w-5 h-5" />
        </button>
      </div>
    );
  }

  return (
    <>
      {/* Loading overlay */}
      {loading && <LoadingOverlay progress={progress} />}
      
      <div className="bg-white rounded-xl p-8 border border-black/5 mb-12" data-testid="lead-capture-form">
      <div className="text-center mb-6">
        <h3 className="font-outfit text-2xl font-bold text-navy mb-3">
          Analyse per E-Mail erhalten
        </h3>
        <p className="text-charcoal leading-relaxed">
          Geben Sie Ihre Daten ein, um Ihre vollständige Auswertung zu erhalten.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="max-w-md mx-auto">
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <Label htmlFor="firstName" className="text-navy font-medium">Vorname</Label>
            <Input
              id="firstName"
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="Max"
              required
              className="mt-2"
              data-testid="lead-firstname-input"
            />
          </div>
          <div>
            <Label htmlFor="lastName" className="text-navy font-medium">Nachname</Label>
            <Input
              id="lastName"
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Mustermann"
              required
              className="mt-2"
              data-testid="lead-lastname-input"
            />
          </div>
        </div>

        <div className="mb-6">
          <Label htmlFor="email" className="text-navy font-medium">E-Mail-Adresse</Label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="ihre@email.de"
            required
            className="mt-2"
            data-testid="lead-email-input"
          />
        </div>

        {/* Privacy checkbox */}
        <div className="flex items-start gap-3 mb-6">
          <Checkbox
            id="privacy"
            checked={privacyAccepted}
            onCheckedChange={setPrivacyAccepted}
            className="mt-0.5"
            data-testid="privacy-checkbox"
          />
          <label htmlFor="privacy" className="text-sm text-slate-600 leading-relaxed cursor-pointer">
            Ich habe die{' '}
            <Link 
              to="/datenschutz" 
              target="_blank" 
              className="text-cobalt hover:text-navy underline underline-offset-2"
            >
              Datenschutzerklärung
            </Link>
            {' '}gelesen und stimme der Verarbeitung meiner Daten zu.
          </label>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg mb-4 text-sm" data-testid="lead-error">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={loading || !privacyAccepted}
          className="w-full px-8 py-4 bg-cobalt text-white rounded-full font-medium hover:bg-navy transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="submit-lead-btn"
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Wird generiert...
            </>
          ) : (
            <>
              <Mail className="w-5 h-5" />
              Analyse zusenden
            </>
          )}
        </button>
      </form>
    </div>
    </>
  );
};
